import * as React from 'react';
import {useState, useEffect, useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {RunningIndicator} from '../StripeAssistant/LiveAnswersV2/RunningIndicator';

const messages = defineMessages({
  thinking: {
    id: 'conversations_ui.loading_text.thinking',
    description: 'The first loading text that shows',
    defaultMessage: 'Thinking...',
  },
  just_one_moment: {
    id: 'conversations_ui.loading_text.just_one_moment',
    description:
      'The second loading text that shows when the first one is done',
    defaultMessage: 'Just one moment...',
  },
  canned_loading_msg_1: {
    id: 'conversations_ui.loading_text.canned_loading_msg_1',
    description: 'Canned loading message 1',
    defaultMessage: 'Analyzing transaction patterns...',
  },
  canned_loading_msg_2: {
    id: 'conversations_ui.loading_text.canned_loading_msg_2',
    description: 'Canned loading message 2',
    defaultMessage: 'Checking your account details...',
  },
  canned_loading_msg_3: {
    id: 'conversations_ui.loading_text.canned_loading_msg_3',
    description: 'Canned loading message 3',
    defaultMessage: 'Reviewing your payment history...',
  },
  canned_loading_msg_4: {
    id: 'conversations_ui.loading_text.canned_loading_msg_4',
    description: 'Canned loading message 4',
    defaultMessage: 'Reviewing transaction logs...',
  },
  canned_loading_msg_5: {
    id: 'conversations_ui.loading_text.canned_loading_msg_5',
    description: 'Canned loading message 5',
    defaultMessage: 'Writing some queries...',
  },
  canned_loading_msg_6: {
    id: 'conversations_ui.loading_text.canned_loading_msg_6',
    description: 'Canned loading message 6',
    defaultMessage: 'Gathering data points...',
  },
  canned_loading_msg_7: {
    id: 'conversations_ui.loading_text.canned_loading_msg_7',
    description: 'Canned loading message 7',
    defaultMessage: 'Checking historic datasets...',
  },
  canned_loading_msg_8: {
    id: 'conversations_ui.loading_text.canned_loading_msg_8',
    description: 'Canned loading message 8',
    defaultMessage: 'Reviewing payment details...',
  },
  canned_loading_msg_9: {
    id: 'conversations_ui.loading_text.canned_loading_msg_9',
    description: 'Canned loading message 9',
    defaultMessage: 'Looking up your data...',
  },
  canned_loading_msg_10: {
    id: 'conversations_ui.loading_text.canned_loading_msg_10',
    description: 'Canned loading message 10',
    defaultMessage: 'Scanning payment records...',
  },
  canned_loading_msg_11: {
    id: 'conversations_ui.loading_text.canned_loading_msg_11',
    description: 'Canned loading message 11',
    defaultMessage: 'Searching for relevant payments...',
  },
  canned_loading_msg_12: {
    id: 'conversations_ui.loading_text.canned_loading_msg_12',
    description: 'Canned loading message 12',
    defaultMessage: 'Checking account history...',
  },
});

type LoadingStage = 'thinking' | 'just_one_moment';

const CANNED_MESSAGE_KEYS = [
  'canned_loading_msg_1',
  'canned_loading_msg_2',
  'canned_loading_msg_3',
  'canned_loading_msg_4',
  'canned_loading_msg_5',
  'canned_loading_msg_6',
  'canned_loading_msg_7',
  'canned_loading_msg_8',
  'canned_loading_msg_9',
  'canned_loading_msg_10',
  'canned_loading_msg_11',
  'canned_loading_msg_12',
] as const;

export function LoadingTextAsync({
  customText,
  stage = 'thinking',
  enableCannedMessageCycling = false,
}: {
  customText?: string;
  stage?: LoadingStage;
  enableCannedMessageCycling?: boolean;
}) {
  const intl = useIntl();
  const [currentMessageKey, setCurrentMessageKey] = useState<string | null>(
    null,
  );
  const [shownMessages, setShownMessages] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!enableCannedMessageCycling || customText) {
      return;
    }

    const timeout = setTimeout(() => {
      const availableMessages = CANNED_MESSAGE_KEYS.filter(
        (key) => !shownMessages.has(key),
      );

      if (availableMessages.length === 0) {
        return;
      }

      const randomIndex = Math.floor(Math.random() * availableMessages.length);
      const selectedKey = availableMessages[randomIndex];

      setCurrentMessageKey(selectedKey);
      setShownMessages((prev) => new Set([...prev, selectedKey]));
    }, 5000);

    return () => clearTimeout(timeout);
  }, [enableCannedMessageCycling, customText, shownMessages]);

  const content = useMemo(() => {
    if (customText) {
      return customText;
    }

    // Only show canned messages for the payments performance subagent
    if (enableCannedMessageCycling && currentMessageKey) {
      return intl.formatMessage(
        messages[currentMessageKey as keyof typeof messages],
      );
    }

    switch (stage) {
      case 'thinking':
        return intl.formatMessage(messages.thinking);
      case 'just_one_moment':
        return intl.formatMessage(messages.just_one_moment);
      default:
        return '';
    }
  }, [intl, customText, enableCannedMessageCycling, currentMessageKey, stage]);

  return <RunningIndicator text={content} testId="loader" />;
}
